import {
  canFinish,
  selectFirstIncompleteRequiredIndex,
} from './shared-responder-v2-core-completion.helpers';
import { selectLatestSettledStepIndex } from './shared-responder-v2-core-edit.helpers';
import type {
  QuestionTurnResult,
  ThreadAction,
} from './shared-responder-v2-core-gateway.types';
import { resolveVisibleMarkupText } from './shared-responder-v2-core-markup-text.helpers';
import { isStepReachable } from './shared-responder-v2-core-reachability.helpers';
import { RESPONDER_PHASES } from './shared-responder-v2-core-session.consts';
import { selectIsStepAnswered } from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import type { ResponderThread } from './shared-responder-v2-core-thread.types';

export function withThread(
  state: ResponderSessionState,
  questionId: string,
  update: (thread: ResponderThread) => ResponderThread
): ResponderSessionState {
  const thread = state.threads[questionId];

  if (thread === undefined) {
    return state;
  }

  return {
    ...state,
    threads: { ...state.threads, [questionId]: update(thread) },
  };
}

/**
 * Every way out of an edit except an accepted one. While the change is still
 * on the wire (`editRollback` held) its outcome owns the draft; otherwise the
 * backend never took it, so the pick from before the edit is put back rather
 * than left unlocked and unsent on a question that reads as answered.
 */
export function exitEdit(state: ResponderSessionState): ResponderSessionState {
  const { editing } = state;

  if (editing.mode !== 'editing') {
    return state;
  }

  const exited: ResponderSessionState = {
    ...state,
    editing: { mode: 'none' },
    currentStepIndex: editing.returnStepIndex,
  };

  return state.threads[editing.questionId]?.editRollback === null
    ? withThread(exited, editing.questionId, (thread) => ({
        ...thread,
        draft: editing.draftBeforeEdit,
      }))
    : exited;
}

/** The sequence axis only; routing is gated by `isResultOnCurrentQuestion`. */
export function isStaleResult(
  state: ResponderSessionState,
  questionId: string,
  seq: number
): boolean {
  const thread = state.threads[questionId];

  return thread === undefined || seq < thread.requestSeq;
}

export function isResultOnCurrentQuestion(
  state: ResponderSessionState,
  questionId: string
): boolean {
  return state.steps[state.currentStepIndex]?.questionId === questionId;
}

const TERMINAL_ACTION_PRECEDENCE = [
  'screenOut',
  'endConversation',
  'redirect',
] as const;

const NO_ACTIONS: readonly ThreadAction[] = Object.freeze([]);

export function selectEffectiveActions(
  result: QuestionTurnResult
): readonly ThreadAction[] {
  return result.isComplete || result.isSurveyComplete
    ? result.actions
    : NO_ACTIONS;
}

export function isSessionEndingTurn(result: QuestionTurnResult): boolean {
  return result.isSurveyComplete || selectEffectiveActions(result).length > 0;
}

function resolveTerminalAction(
  actions: readonly ThreadAction[]
): ThreadAction | null {
  for (const type of TERMINAL_ACTION_PRECEDENCE) {
    const action = actions.find((candidate) => candidate.type === type);

    if (action !== undefined) {
      return action;
    }
  }

  return null;
}

function hasNonBlankEndMessage(actions: readonly ThreadAction[]): boolean {
  return actions.some(
    (action) =>
      action.type === 'endConversation' &&
      resolveVisibleMarkupText(action.message) !== null
  );
}

function resolveActionPhase(
  state: ResponderSessionState,
  action: ThreadAction | null,
  effectiveActions: readonly ThreadAction[]
): ResponderSessionState['phase'] {
  if (action === null) {
    return state.phase;
  }

  const isScreenOutWithoutMessage =
    action.type === 'screenOut' && !hasNonBlankEndMessage(effectiveActions);

  return isScreenOutWithoutMessage
    ? RESPONDER_PHASES.ScreenedOut
    : RESPONDER_PHASES.Outro;
}

function resolveIsComplete(
  state: ResponderSessionState,
  result: QuestionTurnResult,
  args: { readonly isEnded: boolean; readonly isRoutable: boolean }
): boolean {
  if (args.isEnded) {
    return true;
  }

  return args.isRoutable ? result.isSurveyComplete : state.isComplete;
}

/** `state.isComplete` tracks `isSurveyComplete`, never the question-level flag. */
export function applyThreadActions(
  state: ResponderSessionState,
  questionId: string,
  result: QuestionTurnResult
): ResponderSessionState {
  const effectiveActions = selectEffectiveActions(result);
  const terminalAction = resolveTerminalAction(effectiveActions);
  const isRoutable = isResultOnCurrentQuestion(state, questionId);
  const isEnded =
    terminalAction !== null && terminalAction.type !== 'screenOut';

  return {
    ...state,
    phase: resolveActionPhase(state, terminalAction, effectiveActions),
    terminalActions:
      terminalAction !== null ? effectiveActions : state.terminalActions,
    nextQuestionId: isRoutable ? result.nextQuestionId : state.nextQuestionId,
    isComplete: resolveIsComplete(state, result, { isEnded, isRoutable }),
  };
}

function moveTo(
  state: ResponderSessionState,
  index: number
): ResponderSessionState {
  return {
    ...state,
    currentStepIndex: index,
    maxReachedStepIndex: Math.max(state.maxReachedStepIndex, index),
    nextQuestionId: null,
  };
}

/**
 * `nextQuestionId` is the backend's routing decision and outranks "the next
 * index", which is what makes conditional logic work without the kernel ever
 * evaluating a rule itself.
 */
export function resolveNextIndex(state: ResponderSessionState): number {
  if (state.nextQuestionId !== null) {
    const pinned = state.steps.findIndex(
      (step) => step.questionId === state.nextQuestionId
    );

    if (pinned !== -1) {
      return pinned;
    }
  }

  return state.currentStepIndex + 1;
}

function recordJumpedOverSteps(
  state: ResponderSessionState,
  nextIndex: number
): ResponderSessionState {
  const newlySkipped = state.steps
    .slice(state.currentStepIndex + 1, nextIndex)
    .map((step) => step.questionId)
    .filter(
      (questionId) =>
        !selectIsStepAnswered(state, questionId) &&
        !state.skippedQuestionIds.includes(questionId)
    );

  return newlySkipped.length === 0
    ? state
    : {
        ...state,
        skippedQuestionIds: [...state.skippedQuestionIds, ...newlySkipped],
      };
}

export function advance(state: ResponderSessionState): ResponderSessionState {
  if (state.editing.mode === 'editing') {
    return exitEdit(state);
  }

  const nextIndex = resolveNextIndex(state);
  const isLeavingQuestions = nextIndex > state.steps.length - 1;

  if (isLeavingQuestions && !state.isComplete && !canFinish(state)) {
    const target = selectFirstIncompleteRequiredIndex(state);

    return target === null ? state : moveTo(state, target);
  }

  if (isLeavingQuestions || state.isComplete) {
    return { ...state, phase: RESPONDER_PHASES.Outro, nextQuestionId: null };
  }

  const moved = moveTo(recordJumpedOverSteps(state, nextIndex), nextIndex);

  return {
    ...moved,
    resumeStepIndex: Math.max(moved.resumeStepIndex, nextIndex),
  };
}

/**
 * Going back to the step settled last, from the question the backend handed
 * out after it, keeps that routing: Continue returns to the handed-out
 * question rather than the next index, which conditional logic may have
 * skipped. Only a step past the next index is pinned, so a sequential survey
 * keeps an unpinned forward-jump ceiling; a step reached by a rail jump
 * forward is not the resume point, so it was never handed out and pinning it
 * would skip the steps before it.
 */
export function jumpTo(
  state: ResponderSessionState,
  stepIndex: number
): ResponderSessionState {
  if (!isStepReachable(state, stepIndex)) {
    return state;
  }

  const exited = exitEdit(state);
  const moved = moveTo(exited, stepIndex);
  const handedOut = exited.steps[exited.currentStepIndex];

  return handedOut !== undefined &&
    exited.currentStepIndex === exited.resumeStepIndex &&
    stepIndex + 1 < exited.currentStepIndex &&
    stepIndex === selectLatestSettledStepIndex(exited)
    ? { ...moved, nextQuestionId: handedOut.questionId }
    : moved;
}

/**
 * The backend's `surveyCompletionStatus` outranks the locally-derived block
 * `advance` set when it optimistically left the questions: reached only after
 * that optimism turned out wrong, so the responder is returned to `Question`
 * rather than left stranded on the outro it never actually earned.
 */
export function replaceCompletionBlock(
  state: ResponderSessionState,
  questionIds: readonly string[]
): ResponderSessionState {
  const isRuleTerminated =
    state.terminalActions.length > 0 &&
    (state.phase === RESPONDER_PHASES.Outro ||
      state.phase === RESPONDER_PHASES.ScreenedOut);

  if (isRuleTerminated) {
    return state;
  }

  const returned = { ...state, phase: RESPONDER_PHASES.Question };
  const target = selectFirstIncompleteRequiredIndex(returned, questionIds);

  return target === null ? returned : moveTo(returned, target);
}
